<template>
  <label
    :class="{
      'is-checked': checked,
      'is-indeterminate': isIndeterminate,
      'has-error': error,
    }"
    class="checkbox"
  >
    <input
      :id="id"
      :key="id"
      ref="input"
      type="checkbox"
      :checked="checked"
      :name="dataTest"
      :disabled="disabled"
      :indeterminate.prop="isIndeterminate"
      @change="$emit('change', $event.target.checked)"
    >
    <span
      class="checkbox__container"
      :data-test="dataTest"
    >
      <span class="checkbox__icon">
        <v-icon size="16">
          <template v-if="!checked">
            {{ icons.plus }}
          </template>
          <template v-else>
            {{ icons.check }}
          </template>
        </v-icon>
      </span>
      <span
        v-if="$slots.default || (title && title.length > 0)"
        class="checkbox__title"
      >
        <slot>{{ title }}</slot>
      </span>
    </span>
  </label>
</template>

<script>
import {
  mdiPlusCircle,
  mdiCheckCircle,
} from '@mdi/js';
import { uniqueId } from 'lodash';

const icons = {
  plus: mdiPlusCircle,
  check: mdiCheckCircle,
};

export default {
  name: 'checkbox',
  model: {
    prop: 'checked',
    event: 'change',
  },
  props: {
    indeterminate: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    dataTest: {
      type: String,
      default: '',
    },
    checked: {
      type: Boolean,
    },
    required: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      isIndeterminate: this.indeterminate,
      icons,
    };
  },
  computed: {
    localName () {
      return this.name ? this.name.toLowerCase() : uniqueId('checkbox_');
    },
    id () {
      if (!this.scope) return this.localName;
      return `${this.scope}-${this.localName}`;
    },
  },
  watch: {
    checked () {
      this.isIndeterminate = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.checkbox {
  cursor: pointer;
  font-size: 16px;
  line-height: 20px;
  font-weight: 500;
  user-select: none;
  position: relative;

  input {
    width: 0.1px;
    opacity: 0;
    overflow: hidden;
    height: 0.1px;
    position: absolute;
    z-index: -1;
  }

  &__container {
    display: inline-flex;
    background-clip: padding-box;
    background-color: $clr-white;
    border: 1px solid var(--v-primary-base, $clr-primary);
    color: var(--v-text-base, $clr-help-text);
    padding: 5px 15px;
    border-radius: 24px;
    transition-property: color, background-color;
    transition-duration: 0.1s;
    transition-timing-function: ease-in-out;
  }

  input:focus + &__container {
    box-shadow: inset 0 0 0 1px var(--v-primary-base, $clr-primary);
  }

  input:checked + &__container,
  &.is-checked &__container {
    color: $clr-white;
    background-color: var(--v-primary-base, $clr-primary);
  }

  &.is-disabled &__container,
  input:disabled + &__container {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &.is-checked input:focus + &__container {
    opacity: 0.8; // similar to vuetify button focus
  }

  &:not(.is-checked, .is-disabled):hover &__container,
  &:hover input:not(:checked, :disabled) + &__container {
    background-color: rgba(var(--v-primary-rgb, $clr-primary-rgb), 0.18);
    border-color: rgba(var(--v-primary-rgb, $clr-primary-rgb), 0.18);
  }

  &__icon {
    order: 1;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    flex: 0 0 auto;
    border-radius: 50%;
    color: var(--v-primary-base, $clr-primary);
    transition: all 0.1s ease-in-out;
    transition-property: color, background-color;
    position: relative;

    &::v-deep {
      .v-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: currentColor;

        & > svg {
          fill: currentColor;
        }
      }
    }
  }

  &.is-checked &__icon,
  input:checked + &__container &__icon {
    color: $clr-white;
    background-color: var(--v-primary-base, $clr-primary);
  }

  &.has-error input:checked + &__container,
  &.has-error.is-checked &__container {
    background-color: var(--v-error-base, $clr-error);
  }

  &.has-error &__container {
    border: 1px solid var(--v-error-base, $clr-error);
  }

  &.has-error:not(.is-checked, .is-disabled):hover &__container,
  &.has-error:hover input:not(:checked, :disabled) + &__container {
    background-color: rgba(var(--v-error-rgb, $clr-error-rgb), 0.18);
    border-color: rgba(var(--v-error-rgb, $clr-error-rgb), 0.18);
  }

  &.has-error &__icon {
    color: var(--v-error-base, $clr-error);
  }

  &.has-error.is-checked &__icon,
  &.has-error input:checked + &__container &__icon {
    background-color: var(--v-primary-error, $clr-error);
  }

  &__title {
    flex-grow: 1;
    margin-right: 12px;
  }

  // input:indeterminate + &__icon,
  // &.is-indeterminate &__icon {
  //   background: #ccc;
  //   border-color: #ccc;

  //   &::before {
  //     opacity: 1;
  //   }
  // }
}
</style>
